/**
 * Canonical lookups used by the user-story / eval criterion editor in
 * studio. Both the bot runtime and the studio editor read from here so the
 * dropdowns and the runtime-validated values can't drift.
 *
 * - `KNOWN_TOOL_NAMES`  — every tool the LLM may call. Mirror of the Storage
 *   tool registry; update both when adding a new tool.
 * - `KNOWN_STRUCTURED_KINDS` — every structured-message kind the bot may
 *   emit on web. Must align with the runtime `KindRegistry` augmentations
 *   in `libs/types/src/BotChat/Kinds/*.Types.ts`.
 *
 * Keep these as plain string arrays — the studio app can't import from the
 * bot's `ToolRegistry`, and the `KindRegistry` interface is a type-only
 * augmentation with no runtime list.
 */

/** Canonical tool names recognized by the criterion editor. */
export const KNOWN_TOOL_NAMES = [
  "compare",
  "emit_buttons",
  "emit_profile_form",
  "end_call",
  "escalate_to_team",
  "find_locations",
  "forward_call",
  "get_gate_code",
  "get_location_open_status",
  "list_leases",
  "list_units",
  "navigate_to_page",
  "load_lease",
  "load_location",
  "load_unit",
  "lookup_user",
  "lookup_info",
  "manage_account",
  "report_emergency",
  "send_notification",
  "verify_phone"
] as const;

export type KnownToolName = (typeof KNOWN_TOOL_NAMES)[number];

/** Canonical structured-message kinds (web-only). */
export const KNOWN_STRUCTURED_KINDS = ["location-picker", "unit-picker", "buttons", "profile-form"] as const;

export type KnownStructuredKind = (typeof KNOWN_STRUCTURED_KINDS)[number];

/** Criterion types whose verdict needs the toolName field. */
export const TOOL_NAMED_CRITERION_TYPES = ["tool_called", "tool_not_called", "tool_input", "tool_output"] as const;

/** Criterion types whose verdict needs the kind field. */
export const KIND_NAMED_CRITERION_TYPES = ["structured_message_emitted"] as const;

/**
 * Tools that mutate shared eval-org fixture state (Leads, Notes, Messages,
 * verification codes). Used by the eval runner's parallelization logic to
 * sequentialize cases that would otherwise race on the same fixture
 * user/location when run concurrently.
 *
 * Read-only tools (find_locations, load_*, list_*, lookup_*, get_*, compare,
 * end_call, escalate_to_team, forward_call, emit_buttons) are NOT in this set and may run
 * fully in parallel.
 */
export const ACTION_TOOL_NAMES = ["manage_account", "send_notification", "verify_phone"] as const;

export type ActionToolName = (typeof ACTION_TOOL_NAMES)[number];

const ACTION_TOOL_SET = new Set<string>(ACTION_TOOL_NAMES);

export function isActionToolName(name: string | undefined | null): boolean {
  return !!name && ACTION_TOOL_SET.has(name);
}
